import { Box, Circle, MouseJoint, Settings, Vec2, World } from 'planck'

// All Planck access lives in this module. The rest of the game talks to
// PhysicsBody handles, which keep the property names the game code already
// used (position, velocity, bounds, isSleeping, plugin.game, ...).
//
// Units
// - Distance: world units (one builder grid cell is WORLD.grid = 0.55).
// - Time: simulated seconds. timeScale slows simulated time relative to
//   wall-clock time so shots stay readable.
// - Velocity exposed on PhysicsBody is in "step units" (distance per 1/60 s of
//   simulated time). Damage, rest and audio thresholds across the game were
//   tuned in those units, so the handle converts at the boundary.
// - Gravity is configured with the legacy tuning value (e.g. -1.64) and
//   converted to units/s^2 with GRAVITY_SCALE.

export const SIM_STEP_SECONDS = 1 / 480
export const STEP_UNITS_PER_SECOND = 60
export const GRAVITY_SCALE = 1000
// Converts a legacy "force" (as used by the old engine at the game's default
// 60 fps / 0.34 time scale) into an impulse in Planck units.
export const LEGACY_FORCE_TO_IMPULSE = 5667

const VELOCITY_ITERATIONS = 10
const POSITION_ITERATIONS = 8
const MAX_STEPS_PER_FRAME = 24
const DRAG_JOINT_FREQUENCY = 7
const DRAG_JOINT_DAMPING = 0.9
const DRAG_JOINT_FORCE_PER_MASS = 9000

// Simulated speeds here are high (bullets cross the stage in well under a
// simulated second), so the bounce threshold scales up accordingly. Contacts
// slower than this resolve as inelastic, which keeps stacks quiet.
Settings.velocityThreshold = 6
Settings.maxTranslation = 4
// Blocks can be as thin as 0.275 units, so use a finer contact tolerance
// than Planck's metre-scale default.
Settings.linearSlop = 0.0025

// Planck pads every polygon with a thin skin (polygonRadius). Boxes are built
// that much smaller so the visible block size matches the collision size and
// stacks don't creep apart.
const POLYGON_SKIN = Settings.polygonRadius

let nextBodyId = 1

export class PhysicsBody {
    constructor(world, body, { label = '', game = null } = {}) {
        this.world = world
        this.body = body
        this.id = nextBodyId++
        this.label = label
        this.plugin = { game }
        this.removed = false
        // Velocity at the start of the current step (simulated units/s), used
        // for impact speeds: by the time Planck reports a fast projectile's
        // contact, part of the impact may already be resolved.
        this.stepStartVelocity = { x: 0, y: 0 }
        body.setUserData(this)
    }

    get position() {
        const p = this.body.getPosition()
        return { x: p.x, y: p.y }
    }

    get angle() {
        return this.body.getAngle()
    }

    get velocity() {
        const v = this.body.getLinearVelocity()
        return { x: v.x / STEP_UNITS_PER_SECOND, y: v.y / STEP_UNITS_PER_SECOND }
    }

    // Velocity going into the current step's contacts (step units).
    get impactVelocity() {
        return getImpactVelocity(this)
    }

    get angularVelocity() {
        return this.body.getAngularVelocity() / STEP_UNITS_PER_SECOND
    }

    get speed() {
        const v = this.body.getLinearVelocity()
        return Math.hypot(v.x, v.y) / STEP_UNITS_PER_SECOND
    }

    get mass() {
        return this.body.getMass()
    }

    get isStatic() {
        return this.body.isStatic()
    }

    get isSleeping() {
        return this.body.isDynamic() && !this.body.isAwake()
    }

    get bounds() {
        let minX = Infinity
        let minY = Infinity
        let maxX = -Infinity
        let maxY = -Infinity

        for (let fixture = this.body.getFixtureList(); fixture; fixture = fixture.getNext()) {
            const shape = fixture.getShape()

            if (shape instanceof Circle) {
                const center = this.body.getWorldPoint(shape.getCenter())
                const radius = shape.getRadius()
                minX = Math.min(minX, center.x - radius)
                maxX = Math.max(maxX, center.x + radius)
                minY = Math.min(minY, center.y - radius)
                maxY = Math.max(maxY, center.y + radius)
                continue
            }

            for (const vertex of shape.m_vertices) {
                const point = this.body.getWorldPoint(vertex)
                minX = Math.min(minX, point.x)
                maxX = Math.max(maxX, point.x)
                minY = Math.min(minY, point.y)
                maxY = Math.max(maxY, point.y)
            }
        }

        if (!Number.isFinite(minX)) {
            const p = this.body.getPosition()
            return { min: { x: p.x, y: p.y }, max: { x: p.x, y: p.y } }
        }

        return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } }
    }

    setPosition(position, angle = this.body.getAngle()) {
        this.body.setTransform(Vec2(position.x, position.y), angle)
        this.body.setAwake(true)
    }

    setAngle(angle) {
        this.body.setTransform(this.body.getPosition(), angle)
        this.body.setAwake(true)
    }

    setVelocity(velocity) {
        this.body.setLinearVelocity(Vec2(
            velocity.x * STEP_UNITS_PER_SECOND,
            velocity.y * STEP_UNITS_PER_SECOND
        ))
    }

    setAngularVelocity(angularVelocity) {
        this.body.setAngularVelocity(angularVelocity * STEP_UNITS_PER_SECOND)
    }

    // Accepts the game's legacy force values (tuned against the old engine)
    // and applies them as a one-off impulse at the centre of mass.
    applyForce(force) {
        if (!this.body.isDynamic()) {
            return
        }

        this.body.applyLinearImpulse(
            Vec2(force.x * LEGACY_FORCE_TO_IMPULSE, force.y * LEGACY_FORCE_TO_IMPULSE),
            this.body.getWorldCenter(),
            true
        )
    }

    // Adds a velocity change (step units) as an impulse, so heavier bodies
    // need proportionally more to move.
    addVelocity(delta) {
        if (!this.body.isDynamic()) {
            return
        }

        const mass = this.body.getMass()
        this.body.applyLinearImpulse(
            Vec2(delta.x * mass * STEP_UNITS_PER_SECOND, delta.y * mass * STEP_UNITS_PER_SECOND),
            this.body.getWorldCenter(),
            true
        )
    }

    setAwake(awake) {
        if (this.body.isDynamic()) {
            this.body.setAwake(Boolean(awake))
        }
    }

    setStatic(isStatic) {
        this.body.setType(isStatic ? 'static' : 'dynamic')
        if (!isStatic) {
            this.body.setAwake(true)
        }
    }

    setMaterial({ density, friction, restitution, linearDamping, angularDamping } = {}) {
        for (let fixture = this.body.getFixtureList(); fixture; fixture = fixture.getNext()) {
            if (Number.isFinite(density)) fixture.setDensity(density)
            if (Number.isFinite(friction)) fixture.setFriction(friction)
            if (Number.isFinite(restitution)) fixture.setRestitution(restitution)
        }

        if (Number.isFinite(density)) this.body.resetMassData()
        if (Number.isFinite(linearDamping)) this.body.setLinearDamping(linearDamping)
        if (Number.isFinite(angularDamping)) this.body.setAngularDamping(angularDamping)
        this.setAwake(true)
    }
}

export class PhysicsWorld {
    // options.gravity: legacy gravity value (negative is down).
    // options.timeScale: simulated seconds per wall-clock second.
    // options.onContact(event): called after each step for every new contact.
    // options.resolveRestitution(bodyA, bodyB): optional per-contact bounce.
    constructor({ gravity = -1.64, timeScale = 1, onContact = null, resolveRestitution = null } = {}) {
        this.world = new World({ gravity: Vec2(0, gravity * GRAVITY_SCALE) })
        this.timeScale = timeScale
        this.onContact = onContact
        this.resolveRestitution = resolveRestitution
        this.accumulator = 0
        this.pendingContacts = []
        this.anchor = this.world.createBody()
        this.world.on('begin-contact', (contact) => this.recordContact(contact))
    }

    setGravity(gravity) {
        this.world.setGravity(Vec2(0, gravity * GRAVITY_SCALE))
        for (let body = this.world.getBodyList(); body; body = body.getNext()) {
            body.setAwake(true)
        }
    }

    setTimeScale(timeScale) {
        this.timeScale = timeScale
    }

    addBox({
        x,
        y,
        width,
        height,
        angle = 0,
        isStatic = false,
        density = 0.01,
        friction = 0.6,
        restitution = 0,
        linearDamping = 0,
        angularDamping = 0,
        awake = true,
        label = '',
        game = null
    }) {
        const body = this.world.createBody({
            type: isStatic ? 'static' : 'dynamic',
            position: Vec2(x, y),
            angle,
            linearDamping,
            angularDamping,
            awake
        })
        body.createFixture({
            shape: new Box(Math.max(0.01, width / 2 - POLYGON_SKIN), Math.max(0.01, height / 2 - POLYGON_SKIN)),
            density,
            friction,
            restitution
        })

        return new PhysicsBody(this, body, { label, game })
    }

    addCircle({
        x,
        y,
        radius,
        density = 0.01,
        friction = 0.6,
        restitution = 0,
        linearDamping = 0,
        angularDamping = 0,
        bullet = false,
        // Bodies sharing the same negative group never collide with each other.
        group = 0,
        label = '',
        game = null
    }) {
        const body = this.world.createBody({
            type: 'dynamic',
            position: Vec2(x, y),
            linearDamping,
            angularDamping,
            bullet
        })
        body.createFixture({
            shape: new Circle(radius),
            density,
            friction,
            restitution,
            filterGroupIndex: group
        })

        return new PhysicsBody(this, body, { label, game })
    }

    remove(handle) {
        if (!handle || handle.removed) {
            return
        }

        handle.removed = true
        this.world.destroyBody(handle.body)
    }

    // Pulls a body toward a world point with a soft spring. Used for builder
    // drag so dragged blocks still collide with the rest of the stack.
    createDragJoint(handle, point) {
        const body = handle.body
        body.setAwake(true)
        const joint = this.world.createJoint(new MouseJoint({
            maxForce: DRAG_JOINT_FORCE_PER_MASS * body.getMass() * GRAVITY_SCALE / 1000,
            frequencyHz: DRAG_JOINT_FREQUENCY,
            dampingRatio: DRAG_JOINT_DAMPING
        }, this.anchor, body, Vec2(point.x, point.y)))

        return {
            setTarget: (target) => {
                body.setAwake(true)
                joint.setTarget(Vec2(target.x, target.y))
            },
            destroy: () => this.world.destroyJoint(joint)
        }
    }

    // Advances by a wall-clock delta using fixed simulation steps. Contact
    // events are delivered after each step, when bodies may be safely removed.
    step(deltaMs) {
        if (!Number.isFinite(deltaMs) || deltaMs <= 0) {
            return 0
        }

        this.accumulator += (deltaMs / 1000) * this.timeScale
        let steps = 0

        while (this.accumulator >= SIM_STEP_SECONDS && steps < MAX_STEPS_PER_FRAME) {
            this.stepOnce()
            this.accumulator -= SIM_STEP_SECONDS
            steps += 1
        }

        if (steps === MAX_STEPS_PER_FRAME) {
            this.accumulator = 0
        }

        return steps
    }

    stepOnce() {
        for (let body = this.world.getBodyList(); body; body = body.getNext()) {
            if (body.isDynamic() && body.isAwake()) {
                const velocity = body.getLinearVelocity()
                const handle = body.getUserData()
                if (handle) {
                    handle.stepStartVelocity = { x: velocity.x, y: velocity.y }
                }
            }
        }

        this.world.step(SIM_STEP_SECONDS, VELOCITY_ITERATIONS, POSITION_ITERATIONS)
        this.flushContacts()
    }

    // Runs the simulation for a span of simulated seconds without rendering.
    simulate(seconds) {
        const steps = Math.ceil(seconds / SIM_STEP_SECONDS)
        for (let index = 0; index < steps; index += 1) {
            this.stepOnce()
        }
    }

    recordContact(contact) {
        const bodyA = contact.getFixtureA().getBody().getUserData()
        const bodyB = contact.getFixtureB().getBody().getUserData()

        if (!bodyA || !bodyB) {
            return
        }

        if (this.resolveRestitution) {
            const restitution = this.resolveRestitution(bodyA, bodyB)
            if (Number.isFinite(restitution)) {
                contact.setRestitution(Math.min(1, Math.max(0, restitution)))
            }
        }

        const manifold = contact.getWorldManifold(null)
        const normal = manifold?.normal ? { x: manifold.normal.x, y: manifold.normal.y } : null
        const pointCount = contact.getManifold().pointCount
        const supports = (manifold?.points || [])
            .slice(0, pointCount)
            .map((point) => ({ x: point.x, y: point.y }))
        const velocityA = getImpactVelocity(bodyA)
        const velocityB = getImpactVelocity(bodyB)

        this.pendingContacts.push({
            id: getPairId(bodyA, bodyB),
            bodyA,
            bodyB,
            relativeSpeed: Math.hypot(velocityA.x - velocityB.x, velocityA.y - velocityB.y),
            collision: { normal, supports }
        })
    }

    flushContacts() {
        if (!this.pendingContacts.length) {
            return
        }

        const contacts = this.pendingContacts
        this.pendingContacts = []

        if (!this.onContact) {
            return
        }

        for (const contact of contacts) {
            if (contact.bodyA.removed || contact.bodyB.removed) {
                continue
            }

            this.onContact(contact)
        }
    }

    getBodies() {
        const bodies = []
        for (let body = this.world.getBodyList(); body; body = body.getNext()) {
            const handle = body.getUserData()
            if (handle) {
                bodies.push(handle)
            }
        }
        return bodies
    }

    destroy() {
        this.onContact = null
        this.pendingContacts = []
        for (let body = this.world.getBodyList(); body;) {
            const next = body.getNext()
            const handle = body.getUserData()
            if (handle) {
                handle.removed = true
            }
            this.world.destroyBody(body)
            body = next
        }
    }
}

// Integrates a free-flying projectile with the same scheme Planck uses
// (semi-implicit Euler with linear damping), so the aim preview matches the
// real shot exactly when nothing is hit.
export function predictProjectilePath({
    position,
    velocity,
    gravity,
    linearDamping = 0,
    maxSeconds = 3,
    sampleEverySeconds = 1 / 60,
    shouldStop = null
}) {
    const points = []
    const h = SIM_STEP_SECONDS
    const gravityY = gravity * GRAVITY_SCALE
    const damping = 1 / (1 + h * linearDamping)
    const sampleEvery = Math.max(1, Math.round(sampleEverySeconds / h))
    const maxSteps = Math.ceil(maxSeconds / h)
    let x = position.x
    let y = position.y
    let vx = velocity.x * STEP_UNITS_PER_SECOND
    let vy = velocity.y * STEP_UNITS_PER_SECOND

    for (let step = 1; step <= maxSteps; step += 1) {
        vy += h * gravityY
        vx *= damping
        vy *= damping
        x += h * vx
        y += h * vy

        const point = { x, y }
        const stop = shouldStop?.(point)

        if (step % sampleEvery === 0 || stop) {
            points.push(point)
        }

        if (stop) {
            break
        }
    }

    return points
}

// Matter-style per-step air friction (fraction of velocity lost per 1/60 s)
// expressed as Planck linear damping.
export function airFrictionToDamping(frictionAir) {
    const value = Math.min(0.95, Math.max(0, Number(frictionAir) || 0))
    return -Math.log(1 - value) * STEP_UNITS_PER_SECOND
}

// Faster of the step-start and current velocity, in step units.
function getImpactVelocity(handle) {
    const current = handle.body.getLinearVelocity()
    const start = handle.body.isAwake() || handle.body.isStatic() ? handle.stepStartVelocity : { x: 0, y: 0 }
    const use = Math.hypot(start.x, start.y) > Math.hypot(current.x, current.y) ? start : current
    return { x: use.x / STEP_UNITS_PER_SECOND, y: use.y / STEP_UNITS_PER_SECOND }
}

function getPairId(bodyA, bodyB) {
    return bodyA.id < bodyB.id ? `${bodyA.id}:${bodyB.id}` : `${bodyB.id}:${bodyA.id}`
}
